import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Mail, Lock, User, AlertCircle, UserPlus } from 'lucide-react';

const GOVERNO_ANGOLA_LOGO = 'https://customer-assets.emergentagent.com/job_50e85d91-1f1d-48c5-a645-eaeea3c1f4b7/artifacts/xdpxektw_Governo%20de%20Angola%20Logo.png';

export default function Register() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', username: '', full_name: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) {
      setError('As senhas não coincidem');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/register', {
        email: form.email, username: form.username, full_name: form.full_name, password: form.password
      });
      login(res.data.token, res.data.user);
      toast.success('Conta criada com sucesso!');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={GOVERNO_ANGOLA_LOGO} alt="Governo de Angola" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.register_title')}</h1>
          <p className="text-sm text-gray-500 mt-1">Crie a sua conta no portal</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="register-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.full_name')}</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={form.full_name} onChange={update('full_name')} required
                  placeholder="Nome Completo" data-testid="register-fullname"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.username')}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                <input type="text" value={form.username} onChange={update('username')} required
                  placeholder="username" data-testid="register-username"
                  className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={form.email} onChange={update('email')} required
                  placeholder="seu@email.com" data-testid="register-email"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="password" value={form.password} onChange={update('password')} required
                  placeholder="Mínimo 6 caracteres" data-testid="register-password"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirm_password') || 'Confirmar Senha'}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="password" value={form.confirm} onChange={update('confirm')} required
                  placeholder="Repita a senha" data-testid="register-confirm"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#CC0000] text-white font-semibold rounded-md hover:bg-[#A30000] disabled:opacity-60 transition-colors">
              {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <UserPlus size={16} />}
              {loading ? 'Criando conta...' : t('auth.register_btn')}
            </button>
          </form>
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              {t('auth.have_account') || 'Já tem conta?'}{' '}
              <Link to="/login" className="text-[#CC0000] font-medium hover:underline">{t('auth.login_btn')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
